import type { PixiAddress } from "./hooks/usePixiWizardState";
import { isShippingSupported } from "./steps/PaymentShippingStep/utils/supportedShippingCountries";

/**
 * Whether the billing country is one we don't ship hardware to. A blank country
 * is treated as "not yet unsupported" so the warning/edit path only engages once
 * a country is actually set.
 */
export const isBillingUnsupported = (billing: PixiAddress): boolean =>
  !!billing.country.trim() && !isShippingSupported(billing.country);

export interface EffectiveShippingInputs {
  /** Hardware ships for this deal (false for the discount-only deal). */
  needsShipping: boolean;
  /** The "Use the same address for Shipping" toggle. */
  useSameAddress: boolean;
  billing: PixiAddress;
  /** The user's manually-entered shipping address (or a fallback). */
  manualShipping: PixiAddress;
}

/**
 * The single source of truth for the *effective* shipping address across the
 * Payment & Shipping step (display + validation) and the Checkout step (review
 * card + `startSubscription`). The two steps previously derived this
 * independently and disagreed (GRWBKK-1735).
 *
 * "Use the same address for Shipping" means the shipping address *is* the
 * billing address, so while it is ticked the effective shipping is billing —
 * fully, including the country. That holds whether or not billing is a supported
 * destination: the shippable case renders billing read-only, while the
 * unsupported case renders it editable with a warning so the user can change it.
 * Editing the shipping form unticks the toggle (see `usePaymentShippingForm`),
 * at which point the manually-entered shipping address is used instead.
 */
export const resolveEffectiveShipping = ({
  needsShipping,
  useSameAddress,
  billing,
  manualShipping,
}: EffectiveShippingInputs): PixiAddress =>
  needsShipping && useSameAddress ? billing : manualShipping;
